import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { apiFetch } from '../app/auth/apiClient'
import {
  DEFAULT_RECONNECT,
  eventType,
  readEventStream,
  reconnectDelay,
  resumePlan,
  retryConflictResponses,
  sessionStatusFromPayload,
  type ReconnectPolicy,
  type SessionStatus,
} from '@agent-runtime/client'

import {
  applyEvent,
  chatError,
  chatErrorFromResponse,
  freezeRenderedInputs,
  initialChatState,
  inputRecordEvent,
  liveCommentary,
  inputSetIds,
  optimisticUserEvent,
  resumeCursor,
  type ChatErrorCode,
  type ChatState,
  type InputAnswer,
} from './protocol'
import { getScreenData } from './screenData'

type ChatAction = { type: 'event'; event: unknown } | { type: 'reset' }

export const CHAT_CONVERSATION_KEY = 'progetto_arca.codexChat.conversationId'
const CHAT_CONVERSATION_EVENT = 'progetto-arca:chat-conversation'
const CHAT_HISTORY_EVENT = 'progetto-arca:chat-history'
const CONVERSATION_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/

export function currentScreenContext(url?: string) {
  const data = getScreenData(window.location.pathname)
  return {
    url: url ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
    ...(data ? { data } : {}),
  }
}

export function isConversationId(value: unknown): value is string {
  return typeof value === 'string' && CONVERSATION_ID_PATTERN.test(value)
}

export function savedConversationId(): string | null {
  try {
    const stored = localStorage.getItem(CHAT_CONVERSATION_KEY)
    return isConversationId(stored) ? stored : null
  } catch {
    return null
  }
}

export function saveChatConversation(conversationId: string): void {
  if (!isConversationId(conversationId)) return
  try {
    localStorage.setItem(CHAT_CONVERSATION_KEY, conversationId)
  } catch {
    // The in-memory conversation remains available for this session.
  }
}

export function selectChatConversation(
  conversationId: string,
  {
    workspace = false,
    newConversation = false,
  }: { workspace?: boolean; newConversation?: boolean } = {},
): void {
  if (!isConversationId(conversationId)) return
  saveChatConversation(conversationId)
  window.dispatchEvent(
    new CustomEvent(CHAT_CONVERSATION_EVENT, {
      detail: { conversationId, workspace, newConversation },
    }),
  )
}

export function onChatConversationSelected(
  onSelect: (conversationId: string, workspace: boolean, newConversation: boolean) => void,
): () => void {
  const handleSelection = (event: Event) => {
    const detail = (event as CustomEvent<unknown>).detail
    if (isConversationId(detail)) {
      onSelect(detail, false, false)
      return
    }
    if (typeof detail !== 'object' || detail === null) return
    const { conversationId, workspace, newConversation } = detail as {
      conversationId?: unknown
      workspace?: unknown
      newConversation?: unknown
    }
    if (isConversationId(conversationId)) {
      onSelect(conversationId, workspace === true, newConversation === true)
    }
  }
  window.addEventListener(CHAT_CONVERSATION_EVENT, handleSelection)
  return () => window.removeEventListener(CHAT_CONVERSATION_EVENT, handleSelection)
}

export function notifyChatHistoryChanged(): void {
  window.dispatchEvent(new Event(CHAT_HISTORY_EVENT))
}

export function onChatHistoryChanged(onChange: () => void): () => void {
  window.addEventListener(CHAT_HISTORY_EVENT, onChange)
  return () => window.removeEventListener(CHAT_HISTORY_EVENT, onChange)
}

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  return action.type === 'reset' ? initialChatState() : applyEvent(state, action.event)
}

type Dispatch = (action: ChatAction) => void

function errorEvent(code: ChatErrorCode) {
  return { type: 'chat_error', error: chatError(code) }
}

// The backend reports a broken runner hop as an `error` frame with this code
// and then closes the stream without an `end`. The turn is still running, so
// the client re-attaches instead of showing the message.
function isRecoverableTransportError(event: unknown): boolean {
  return (
    typeof event === 'object' &&
    event !== null &&
    (event as { type?: unknown }).type === 'error' &&
    (event as { code?: unknown }).code === 'stream_interrupted'
  )
}

// Read a turn stream to its terminal `end` frame. Returns false when the
// stream closed early (no `end` seen) — the caller re-attaches then.
async function readTurnStream(
  response: Response,
  onEvent: (event: unknown) => void,
  signal?: AbortSignal,
): Promise<boolean> {
  if (!response.body) return false
  let ended = false
  await readEventStream(
    response.body,
    (event) => {
      if (isRecoverableTransportError(event)) return
      if (eventType(event) === 'end') ended = true
      onEvent(event)
    },
    signal,
  )
  return ended
}

// Re-attach policy: the shared client's bounded attempts with a doubling
// delay. Exposed so tests can shorten it; production keeps the defaults.
export const reattachPolicy: ReconnectPolicy = { ...DEFAULT_RECONNECT }

export type TurnStatus = SessionStatus

// Ask the backend (which asks the runner) whether the turn is still running.
// Null when the answer is unavailable: the transcript decides then.
async function fetchTurnStatus(
  conversationId: string,
  signal: AbortSignal,
): Promise<TurnStatus | null> {
  try {
    const response = await apiFetch(`/api/chat/${conversationId}/status`, { signal })
    if (!response.ok) return null
    return sessionStatusFromPayload(await response.json())
  } catch (error) {
    if (isAbortError(error)) throw error
    return null
  }
}

// Tail the turn from the last message this client holds: the backend
// replays what came after it and then continues live. Every event carries
// its uuid, so anything already shown is dropped by the reducer. A 409
// means a send of this conversation is claiming the turn right now.
function resumeResponse(
  conversationId: string,
  after: string | null,
  signal: AbortSignal,
): Promise<Response> {
  const query = after ? `&after=${encodeURIComponent(after)}` : ''
  return retryConflictResponses(() =>
    apiFetch(`/api/chat/${conversationId}/messages?stream=true${query}`, { signal }),
  )
}

type ReconnectContext = {
  conversationId: string
  signal: AbortSignal
  dispatch: Dispatch
  state: () => ChatState
  setReconnecting: (value: boolean) => void
  // Called once the turn is known to be worth tailing.
  onAttach: () => void
  // The server accepted a turn on this connection: tail it even when its
  // `user` echo never arrived and the status route is unreachable.
  assumeOpen?: boolean
}

// Bring the view up to date with the turn on the server. Status first: an
// idle turn with a complete transcript needs nothing; otherwise tail the
// stream until its `end`, re-attaching with backoff when the transport
// breaks. Never starts a second turn.
async function reconnectTurn(context: ReconnectContext): Promise<void> {
  const { conversationId, signal, dispatch, state, setReconnecting, onAttach, assumeOpen } = context
  try {
    for (let attempt = 1; attempt <= reattachPolicy.attempts; attempt += 1) {
      if (attempt > 1) {
        setReconnecting(true)
        const delay = reconnectDelay(attempt - 1, reattachPolicy) ?? reattachPolicy.maxMs
        await new Promise((resolve) => setTimeout(resolve, delay))
        if (signal.aborted) return
      }
      const status = await fetchTurnStatus(conversationId, signal)
      if (signal.aborted) return
      // Nothing runs and the transcript is complete: the view is current.
      const open = state().turnOpen || assumeOpen === true
      const plan = status ? resumePlan(status) : null
      if (!open && plan !== 'tail') return
      if (status?.state === 'missing') {
        dispatch({ type: 'event', event: errorEvent('not_found') })
        dispatch({ type: 'event', event: { type: 'end', status: 'failed' } })
        return
      }
      onAttach()
      try {
        const response = await resumeResponse(conversationId, resumeCursor(state()), signal)
        if (!response.ok) {
          if (response.status >= 400 && response.status < 500 && response.status !== 409) {
            const body = await responseBody(response)
            dispatch({
              type: 'event',
              event: errorEvent(chatErrorFromResponse(response.status, body).code),
            })
            dispatch({ type: 'event', event: { type: 'end', status: 'failed' } })
            return
          }
          continue
        }
        // Attached again: the answer continues in place from here.
        setReconnecting(false)
        if (await readTurnStream(response, (event) => dispatch({ type: 'event', event }), signal)) {
          return
        }
      } catch (error) {
        if (isAbortError(error)) throw error
        console.error(`chat re-attach ${attempt}/${reattachPolicy.attempts} failed`, error)
      }
    }
    // The turn may still run on the server: Retry or the next focus asks again.
    dispatch({ type: 'event', event: errorEvent('reattach_failed') })
    dispatch({ type: 'event', event: { type: 'end', status: 'failed' } })
  } finally {
    setReconnecting(false)
  }
}

export function newConversationId(): string {
  return `c-${Math.random().toString(36).slice(2, 10)}`
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

// The backend answers a refused request with `detail` as a string or, for a
// runner refusal, as `{error, message}`; the shared mapping reads both.
async function responseBody(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

// Load the recorded answers of every input set the transcript published, so
// a reload shows what was already answered.
async function loadInputRecords(
  conversationId: string,
  events: unknown[],
  signal: AbortSignal,
  dispatch: Dispatch,
): Promise<void> {
  for (const setId of inputSetIds(events)) {
    try {
      const response = await apiFetch(`/api/chat/${conversationId}/input-sets/${setId}`, { signal })
      if (!response.ok) continue
      const payload = (await response.json()) as { records?: unknown }
      if (!Array.isArray(payload.records)) continue
      for (const record of payload.records) {
        const event = inputRecordEvent(record)
        if (event) dispatch({ type: 'event', event })
      }
    } catch (error) {
      if (isAbortError(error)) return
      // Answers that do not load render as unanswered; the user can answer again.
    }
  }
}

// A body that names an error code, as the backend does for runner refusals.
function codedBody(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false
  const detail = (body as { detail?: unknown }).detail
  const carrier = typeof detail === 'object' && detail !== null ? detail : body
  return typeof (carrier as { error?: unknown }).error === 'string'
}

type LastSend = { prompt: string; files: File[] }

export function useCodexChat(conversationId: string, screenContextUrl?: string) {
  // The reducer state lives in a ref as well, so the async flows read the
  // transcript as it is right now (cursor, open turn) rather than as of the
  // last render.
  const [state, setState] = useState<ChatState>(initialChatState)
  const stateRef = useRef(state)
  const dispatch = useCallback<Dispatch>((action) => {
    stateRef.current = chatReducer(stateRef.current, action)
    setState(stateRef.current)
  }, [])
  const readState = useCallback(() => stateRef.current, [])

  const [running, setRunning] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  // Every stream open for this conversation: the turn stream of a send, the
  // stream of a steering send, or a re-attach. The flag says whether the
  // stream carries a turn (a status probe that finds nothing does not);
  // the chat is running while any such stream is open.
  const streamsRef = useRef(new Map<AbortController, boolean>())
  const mountedRef = useRef(true)
  const lastSendRef = useRef<LastSend | null>(null)

  const syncRunning = useCallback(() => {
    if (!mountedRef.current) return
    setRunning([...streamsRef.current.values()].some(Boolean))
  }, [])
  const openStream = useCallback(
    (turn: boolean) => {
      const controller = new AbortController()
      streamsRef.current.set(controller, turn)
      syncRunning()
      return controller
    },
    [syncRunning],
  )
  const markTurnStream = useCallback(
    (controller: AbortController) => {
      if (!streamsRef.current.has(controller)) return
      streamsRef.current.set(controller, true)
      syncRunning()
    },
    [syncRunning],
  )
  const closeStream = useCallback(
    (controller: AbortController) => {
      streamsRef.current.delete(controller)
      syncRunning()
    },
    [syncRunning],
  )
  const abortStreams = useCallback(() => {
    for (const controller of streamsRef.current.keys()) controller.abort()
    streamsRef.current.clear()
    syncRunning()
  }, [syncRunning])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortStreams()
    }
  }, [abortStreams])

  // Ask the server where the turn stands and catch up. Used on load, when
  // the tab comes back, when the network returns, and by Retry.
  const reconnect = useCallback(async () => {
    if (streamsRef.current.size > 0) return
    const controller = openStream(false)
    try {
      await reconnectTurn({
        conversationId,
        signal: controller.signal,
        dispatch,
        state: readState,
        setReconnecting,
        onAttach: () => markTurnStream(controller),
      })
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('chat resume failed', error)
        dispatch({ type: 'event', event: errorEvent('reattach_failed') })
      }
    } finally {
      closeStream(controller)
    }
  }, [closeStream, conversationId, dispatch, markTurnStream, openStream, readState])

  useEffect(() => {
    const controller = new AbortController()
    abortStreams()
    lastSendRef.current = null

    // The previous conversation stays on screen until the new history arrives,
    // then the swap happens in one render — no unmount flash while switching.
    void (async () => {
      let events: unknown[] = []
      try {
        const response = await apiFetch(`/api/chat/${conversationId}/messages`, {
          signal: controller.signal,
        })
        if (response.ok) {
          const payload = (await response.json()) as { events?: unknown }
          if (Array.isArray(payload.events)) events = payload.events
        }
      } catch {
        // A missing or unreachable history is an empty conversation, not a chat error.
      }
      if (controller.signal.aborted) return
      dispatch({ type: 'reset' })
      for (const event of events) dispatch({ type: 'event', event })
      setHistoryLoaded(true)
      void loadInputRecords(conversationId, events, controller.signal, dispatch)
      // The transcript may end without an `end`: the turn is running, or it
      // finished while nobody watched. Either way the server knows.
      void reconnect()
    })()

    return () => controller.abort()
  }, [abortStreams, conversationId, dispatch, reconnect])

  // A tab that wakes up or a network that comes back asks the server where
  // the turn stands; a stream already open is already up to date.
  useEffect(() => {
    if (!historyLoaded) return
    const wake = () => {
      if (document.visibilityState === 'hidden') return
      void reconnect()
    }
    window.addEventListener('focus', wake)
    window.addEventListener('online', wake)
    document.addEventListener('visibilitychange', wake)
    return () => {
      window.removeEventListener('focus', wake)
      window.removeEventListener('online', wake)
      document.removeEventListener('visibilitychange', wake)
    }
  }, [historyLoaded, reconnect])

  const respondToInput = useCallback(
    async (
      requestId: string,
      outcome: InputAnswer['outcome'],
      value: unknown = null,
    ): Promise<boolean> => {
      try {
        const response = await apiFetch(`/api/chat/${conversationId}/inputs/${requestId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ outcome, value }),
        })
        if (!response.ok) {
          const body = await responseBody(response)
          const code = codedBody(body) ? chatErrorFromResponse(response.status, body).code : 'input_failed'
          dispatch({ type: 'event', event: errorEvent(code) })
          return false
        }
        const event = inputRecordEvent(await response.json())
        if (event) dispatch({ type: 'event', event })
        return true
      } catch (error) {
        console.error('chat input response failed', error)
        dispatch({ type: 'event', event: errorEvent('input_failed') })
        return false
      }
    },
    [conversationId, dispatch],
  )

  // Send a message. While a turn runs the runner steers it with this
  // message (the answer continues in place); otherwise a new turn starts.
  // The backend closes every open input set before the turn, so the
  // answers recorded so far reach the agent with this message.
  const send = useCallback(
    async (rawPrompt: string, files: File[] = []) => {
      const prompt = rawPrompt
      if (prompt.trim() === '' && files.length === 0) return

      lastSendRef.current = { prompt: rawPrompt, files }
      const controller = openStream(true)
      dispatch({ type: 'event', event: optimisticUserEvent(prompt, files) })

      let streaming = false
      try {
        const form = new FormData()
        form.set('prompt', prompt)
        form.set('screen_context', JSON.stringify(currentScreenContext(screenContextUrl)))
        files.forEach((file) => form.append('attachments', file))
        const response = await apiFetch(`/api/chat/${conversationId}/messages`, {
            method: 'POST',
            body: form,
            signal: controller.signal,
          })
        if (!response.ok) {
          const body = await responseBody(response)
          dispatch({ type: 'event', event: { type: 'retract_pending' } })
          dispatch({
            type: 'event',
            event: errorEvent(chatErrorFromResponse(response.status, body).code),
          })
          return
        }

        // The backend froze the rendered input sets before starting (or
        // steering) the turn: their cards are read-only from here on.
        dispatch({ type: 'event', event: freezeRenderedInputs(readState()) })
        streaming = true
        let historyAnnounced = false
        let ended = false
        try {
          ended = await readTurnStream(
            response,
            (event) => {
              if (!historyAnnounced && eventType(event) === 'user') {
                historyAnnounced = true
                notifyChatHistoryChanged()
              }
              dispatch({ type: 'event', event })
            },
            controller.signal,
          )
        } catch (error) {
          if (isAbortError(error)) throw error
          // A break during the answer is not the end of the turn: the turn
          // keeps running on the server, so re-attach instead of asking for
          // a reload. Never surface the raw exception to the user.
          console.error('chat stream interrupted', error)
        }
        if (!historyAnnounced) notifyChatHistoryChanged()
        if (!ended) {
          await reconnectTurn({
            conversationId,
            signal: controller.signal,
            dispatch,
            state: readState,
            setReconnecting,
            onAttach: () => {},
            assumeOpen: true,
          })
        }
      } catch (error) {
        if (!isAbortError(error)) {
          console.error('chat send failed', error)
          if (!streaming) dispatch({ type: 'event', event: { type: 'retract_pending' } })
          dispatch({
            type: 'event',
            event: errorEvent(streaming ? 'reattach_failed' : 'send_failed'),
          })
        }
      } finally {
        closeStream(controller)
      }
    },
    [closeStream, conversationId, dispatch, openStream, readState, screenContextUrl],
  )

  // Stop the running turn. The open stream delivers its `end` (cancelled).
  const cancel = useCallback(async () => {
    try {
      const response = await apiFetch(`/api/chat/${conversationId}/turn`, { method: 'DELETE' })
      // 404: nothing was running any more; the stream ends on its own.
      if (!response.ok && response.status !== 404) {
        dispatch({ type: 'event', event: errorEvent('cancel_failed') })
      }
    } catch (error) {
      console.error('chat cancel failed', error)
      dispatch({ type: 'event', event: errorEvent('cancel_failed') })
    }
  }, [conversationId, dispatch])

  // After a reload the last send is gone, but the transcript keeps a failed
  // message ahead of its failure: that bubble is resendable when it is the
  // last message (an answered one is not) and carries text (the files it
  // carried stay with the failed turn).
  const resendableText = useCallback((): string | null => {
    const messages = stateRef.current.messages
    const last = messages[messages.length - 1]
    if (!last || last.role !== 'user' || last.pending) return null
    return last.text.trim() === '' ? null : last.text
  }, [])

  // Act on a retryable error: re-attach when the connection was lost,
  // otherwise send the last message again.
  const retry = useCallback(async () => {
    const error = stateRef.current.error
    if (!error?.retryable) return
    if (error.code === 'reattach_failed') {
      await reconnect()
      return
    }
    const last = lastSendRef.current
    if (last !== null) {
      await send(last.prompt, last.files)
      return
    }
    const text = resendableText()
    if (text !== null) {
      await send(text)
      return
    }
    await reconnect()
  }, [reconnect, resendableText, send])

  // Riprova is offered only where it can act: a turn failure with nothing
  // to resend after a reload would be an inert button.
  const error = useMemo(() => {
    const current = state.error
    if (!current?.retryable || current.code === 'reattach_failed') return current
    if (lastSendRef.current !== null || resendableText() !== null) return current
    return { ...current, retryable: false }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads refs on each state change
  }, [state.error, state.messages, resendableText])

  return {
    messages: state.messages,
    activities: state.activities,
    commentary: liveCommentary(state),
    error,
    inputs: state.inputs,
    turnMode: state.turnMode,
    running,
    reconnecting,
    send,
    cancel,
    retry,
    respondToInput,
    historyLoaded,
  }
}
